import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase, supabaseUrl, supabaseAnonKey } from '../utils/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getBasePath } from '../utils/config';
import { updateCachedSessionToken, checkServerHealth } from '../utils/api';
import ZaggerLogo from '../components/ZaggerLogo';
import './LoginPage.css';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [isSignUp, setIsSignUp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUserFromSupabase, backendError, retryLoadUser, loading: authLoading } = useAuth();
  const [serverOnline, setServerOnline] = useState(true); // optimistic default avoids flicker
  const [checkingServer, setCheckingServer] = useState(false);

  // Check backend server reachability on mount and every 10 s
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled) return;
      setCheckingServer(true);
      const ok = await checkServerHealth();
      if (!cancelled) {
        setServerOnline(ok);
        setCheckingServer(false);
      }
    };
    check();
    const interval = setInterval(check, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/projects';
      navigate(from, { replace: true });
    }
  }, [user, navigate, location]);

  // Check for email confirmation or password reset (hash or PKCE ?code=)
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const pkceCode = searchParams.get('code');
    if (pkceCode) {
      console.log('🔍 PKCE code on login page — forwarding to reset password');
      const basePath = getBasePath();
      window.location.replace(`${basePath}/auth/reset-password?code=${encodeURIComponent(pkceCode)}`);
      return;
    }

    const hash = window.location.hash;
    if (!hash || hash.length < 2) {
      return; // No hash, nothing to process
    }
    
    const hashParams = new URLSearchParams(hash.substring(1));
    const type = hashParams.get('type');
    const accessToken = hashParams.get('access_token');
    const errorDescription = hashParams.get('error_description');

    if (type === 'recovery' && accessToken) {
      console.log('🔍 Password reset link detected, redirecting to reset password page');
      const basePath = getBasePath();
      window.location.href = `${basePath}/auth/reset-password${hash}`;
    } else if (type === 'recovery') {
      setMessage('Password reset link detected. Please check your email and follow the instructions.');
    } else if (errorDescription) {
      setError(errorDescription);
    } else if (accessToken) {
      // Handle OAuth callback or email confirmation
      setMessage('Authentication successful! Redirecting...');
      setTimeout(() => {
        window.location.hash = '';
        window.location.reload();
      }, 1000);
    }
  }, []);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        if (signInError.message.includes('Email not confirmed')) {
          setError('Please check your email and confirm your account before signing in.');
        } else if (signInError.message.includes('Invalid login credentials')) {
          setError('Invalid email or password. Please check your credentials and try again.');
        } else {
          setError(signInError.message);
        }
        setLoading(false);
        return;
      }

      if (data?.user && data?.session) {
        // User signed in successfully
        // Cache the session token immediately to avoid race conditions
        const expiresAt = data.session.expires_at ? data.session.expires_at * 1000 : null;
        updateCachedSessionToken(data.session.access_token, expiresAt);
        console.log('✅ Cached session token immediately after sign-in');
        
        // Now load user data from backend
        // Navigation is handled by the useEffect([user]) watcher below,
        // which fires only when user becomes non-null (backend load succeeded).
        await setUserFromSupabase(data.user);
      }
    } catch (err) {
      console.error('Sign in error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      // Check if user is invited (this would be handled server-side)
      // For now, we'll show an error that sign-ups are invite-only
      setError('New accounts are invite-only. Please contact an administrator to receive an invitation.');
      setLoading(false);
      return;

      // If invite system is implemented, uncomment below:
      /*
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`
        }
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      if (data?.user) {
        setMessage('Please check your email to confirm your account. Once confirmed, you can sign in.');
        setIsSignUp(false);
      }
      */
    } catch (err) {
      console.error('Sign up error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);

    if (!email) {
      setError('Please enter your email address to reset your password.');
      setLoading(false);
      return;
    }

    try {
      // Build redirect URL so Supabase sends user to our app WITH the token in the hash.
      // Must include base path (e.g. /casc) or user lands on wrong path and the hash can be lost on redirect.
      const envUrl = process.env.REACT_APP_URL ||
        process.env.APP_URL ||
        process.env.FRONTEND_URL ||
        null;
      const basePath = getBasePath();
      const origin = envUrl ? new URL(envUrl).origin : window.location.origin;
      const redirectUrl = `${origin}${basePath}/auth/reset-password`;

      console.log('Sending password reset email with redirect URL:', redirectUrl, '(base path:', basePath + ')');

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (resetError) {
        setError(resetError.message);
      } else {
        setMessage('Password reset email sent! Please check your inbox and follow the instructions.');
      }
    } catch (err) {
      console.error('Password reset error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check if Supabase is configured
  if (!supabaseUrl || !supabaseAnonKey) {
    return (
      <div className="login-page">
        <div className="login-container">
          <div className="alert alert-error">
            <h2>Configuration Error</h2>
            <p>Supabase authentication is not configured.</p>
            <p>Please set the following environment variables:</p>
            <ul style={{ textAlign: 'left', marginTop: '10px' }}>
              <li>REACT_APP_SUPABASE_URL</li>
              <li>REACT_APP_SUPABASE_ANON_KEY</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <ZaggerLogo variant="full" darkMode={false} />
          <p>Complete Business Data Management</p>
        </div>

        <div className="login-form-container">
          {/* Backend server connectivity status */}
          {!serverOnline && (
            <div className="alert alert-error">
              <strong>Server is currently unreachable.</strong>
              <br />
              The backend server is not responding. Please try again later.
              <button
                type="button"
                className="btn-link"
                style={{ display: 'block', marginTop: 8 }}
                onClick={async () => {
                  setCheckingServer(true);
                  const ok = await checkServerHealth();
                  setServerOnline(ok);
                  setCheckingServer(false);
                }}
                disabled={checkingServer}
              >
                {checkingServer ? 'Checking…' : 'Retry connection'}
              </button>
            </div>
          )}

          {/* Backend returned an error after Supabase auth succeeded */}
          {serverOnline && backendError && (
            <div className="alert alert-error">
              Could not load your account — the server may be starting up or temporarily unavailable.
              <button
                type="button"
                className="btn-link"
                style={{ display: 'block', marginTop: 8 }}
                onClick={retryLoadUser}
                disabled={authLoading}
              >
                {authLoading ? 'Loading…' : 'Retry'}
              </button>
            </div>
          )}

          {error && (
            <div className="alert alert-error">
              {error}
            </div>
          )}

          {message && (
            <div className="alert alert-success">
              {message}
            </div>
          )}

          <form onSubmit={isSignUp ? handleSignUp : handleSignIn}>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                required
                disabled={loading}
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required={!isSignUp}
                disabled={loading}
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || !serverOnline}
            >
              {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Sign In')}
            </button>
          </form>

          <div className="login-actions">
            {!isSignUp && (
              <button
                type="button"
                className="btn-link"
                onClick={handlePasswordReset}
                disabled={loading}
              >
                Forgot password?
              </button>
            )}
            {isSignUp && (
              <button
                type="button"
                className="btn-link"
                onClick={() => setIsSignUp(false)}
                disabled={loading}
              >
                Back to sign in
              </button>
            )}
          </div>
        </div>

        <div className="login-footer">
          <p className="text-muted">
            This is an invite-only system. Contact an administrator for access.
          </p>
        </div>
      </div>
    </div>
  );
}

export default LoginPage;
